/**
 * src/utils/dateUtils.js
 * Utilitaire centralisé pour la gestion robuste des dates sur mobile (Safari iOS, Android WebView)
 * et la prévention des bugs liés aux changements d'heure et aux fuseaux horaires.
 */

/**
 * Vérifie de manière stricte si une valeur est un objet Date valide.
 * @param {any} date - La valeur à tester.
 * @returns {boolean} - true si c'est une Date valide, sinon false.
 */
export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Parse une date de manière sécurisée pour éviter les comportements inconsistants
 * (notamment sur Safari iOS et Android WebView avec new Date()).
 * 
 * Comportement :
 * - Si la valeur contient un indicateur de timezone ('Z', '+02:00'), elle est parsée telle quelle (respect du fuseau).
 * - Si la valeur est naïve ("YYYY-MM-DD HH:mm:ss" ou "YYYY-MM-DDTHH:mm:ss"), elle est interprétée 
 *   manuellement en heure locale.
 * 
 * @param {string|Date} value - La date à parser (chaîne de l'API ou objet Date).
 * @returns {Date|null} - Un objet Date valide ou null si invalide.
 */
export const parseDateSafe = (value) => {
  if (!value) return null;
  if (isValidDate(value)) return value;
  
  if (typeof value === 'string') {
    // Si la chaîne contient explicitement un fuseau horaire (Z ou +02:00)
    // On laisse le navigateur faire, car le format ISO-8601 complet est bien supporté.
    if (value.match(/(Z|[+-]\d{2}:\d{2})$/)) {
      const parsed = new Date(value);
      return isValidDate(parsed) ? parsed : null;
    }

    // Extraction manuelle des composants pour les dates naïves
    // Supporte "2024-05-22 09:00:00", "2024-05-22T09:00:00", "2024-05-22"
    const regex = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/;
    const match = value.match(regex);
    
    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // Les mois commencent à 0
      const day = parseInt(match[3], 10);
      const hours = match[4] ? parseInt(match[4], 10) : 0;
      const minutes = match[5] ? parseInt(match[5], 10) : 0;
      const seconds = match[6] ? parseInt(match[6], 10) : 0;
      const milliseconds = match[7] ? parseInt(match[7].padEnd(3, '0').slice(0, 3), 10) : 0;
      
      const parsed = new Date(year, month, day, hours, minutes, seconds, milliseconds);
      return isValidDate(parsed) ? parsed : null;
    }
    
    // Fallback standard
    const fallbackDate = new Date(value);
    return isValidDate(fallbackDate) ? fallbackDate : null;
  }
  
  return null;
};

/**
 * Formate une date en chaîne locale sécurisée.
 * @param {string|Date} value - La date à formater.
 * @param {Intl.DateTimeFormatOptions} options - Les options de formatage.
 * @returns {string} - La date formatée ou "-" si invalide.
 */
export const formatDateSafe = (value, options = {}) => {
  const date = parseDateSafe(value);
  if (!date) {
    if (process.env.NODE_ENV === 'development' && value) {
      console.warn(`formatDateSafe: Date invalide reçue ->`, value);
    }
    return "-";
  }
  try {
    return date.toLocaleDateString('fr-FR', options);
  } catch (err) {
    console.error("formatDateSafe error", err);
    return "-";
  }
};

/**
 * Formate une heure en chaîne locale sécurisée (HH:mm).
 * @param {string|Date} value - La date/heure à formater.
 * @param {Intl.DateTimeFormatOptions} options - Les options de formatage.
 * @returns {string} - L'heure formatée ou "-" si invalide.
 */
export const formatTimeSafe = (value, options = { hour: '2-digit', minute: '2-digit' }) => {
  const date = parseDateSafe(value);
  if (!date) {
    if (process.env.NODE_ENV === 'development' && value) {
      console.warn(`formatTimeSafe: Heure invalide reçue ->`, value);
    }
    return "-";
  }
  try {
    return date.toLocaleTimeString('fr-FR', options);
  } catch (err) {
    console.error("formatTimeSafe error", err);
    return "-";
  }
};

/**
 * Calcule l'écart en jours calendaires stricts entre deux dates, indépendamment 
 * des heures locales et des changements d'heure été/hiver.
 * 
 * @param {string|Date} date - La date cible.
 * @param {string|Date} startDate - La date de référence (le jour 0).
 * @returns {number|null} - L'écart en jours (positif ou négatif) ou null si invalide.
 */
export const getCalendarDayOffset = (date, startDate) => {
  const d1 = parseDateSafe(date);
  const d2 = parseDateSafe(startDate);
  
  if (!d1 || !d2) return null;

  // On convertit les deux dates en dates calendaires UTC à minuit exact
  // Cela élimine complètement les variations dues au DST local.
  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());

  return Math.round((utc1 - utc2) / (1000 * 60 * 60 * 24));
};

/**
 * Génère une clé calendaire locale au format "YYYY-MM-DD".
 * N'utilise PAS toISOString().slice(0,10) car cela convertirait l'heure locale en UTC
 * avant de couper, ce qui causerait un décalage si l'heure locale est proche de minuit.
 * 
 * @param {string|Date} value - La date à convertir.
 * @returns {string|null} - La clé "YYYY-MM-DD" ou null si invalide.
 */
export const toCalendarDateKey = (value) => {
  const date = parseDateSafe(value);
  if (!date) return null;
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Formate un nombre d'heures décimal en format d'heures réelles (ex: 1.5 -> "1h30", 1.25 -> "1h15").
 * @param {number} hours - Le nombre d'heures sous forme de décimale.
 * @returns {string} - Les heures au format "XhXX" ou "Xh" si pas de minutes.
 */
export const formatDecimalHours = (hours) => {
  if (typeof hours !== 'number' || isNaN(hours)) return '0h';
  
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(Math.abs(totalMinutes) / 60);
  const m = Math.abs(totalMinutes) % 60;
  
  const sign = totalMinutes < 0 ? '-' : '';
  
  if (m === 0) {
    return `${sign}${h}h`;
  }
  return `${sign}${h}h${String(m).padStart(2, '0')}`;
};

